/**
 * Unknown senders (a filter of each triage area tab: what was sent to your personal triage email address, the space's, or both): mail from senders the organization doesn't know yet, held apart from the triage area (nothing runs on it
 * until it's let in). Providers' forwarding confirmations first, as cards with their code and link (opened by you,
 * never followed for you); then the rest by sender, each message's preview opening in place — its text as text, the
 * way the email page shows mail. Admins allow a sender (org-wide: their mail held anywhere is taken in, as just
 * received) or block it; whoever sees a message (its personal triage email address's member, the admins the space's) can accept just
 * those messages or delete them. Left alone, it's deleted after a while.
 */
import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Allowed, Blocked, HeldDone, HeldMessage, HeldSender, Screen, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { formatRelative } from "../lib/format.ts";
import { orgApi, spaceApi } from "../lib/paths.ts";
import { bump, useAction, useResource } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { toast } from "../lib/toast.ts";
import { Button } from "../ui/Button.tsx";
import { Chevron } from "../ui/Chevron.tsx";
import { ConfirmButton, Dialog, DialogActions } from "../ui/Dialog.tsx";
import { Disclosure } from "../ui/Disclosure.tsx";
import { Checkbox, Radios } from "../ui/form.tsx";
import { Async, Badge, CopyButton, EmptyState } from "../ui/misc.tsx";
import { buttonClass } from "../ui/styles.ts";
import { EmailView } from "./item/email.tsx";

/** Whose triage email address the held mail was sent to: your personal one, or the space's (admins). */
export type HeldScope = "personal" | "shared";

/** What the screen acts with: the space, the triage email address(es) shown, and how to show the change. */
interface Env {
  space: SpaceOut;
  scope: HeldScope;
  onChange: () => void;
}

/** Lets messages in (`accept`) or deletes them, then says so. */
function useHeldAction(env: Env, what: "accept" | "delete", ids: readonly string[]) {
  return useAction(async () => {
    const r = await api.post<HeldDone>(spaceApi(env.space, `/held/${what}`), { ids });
    toast(t(what === "accept" ? "held.accepted" : "held.deleted", { count: r.count }));
    env.onChange();
  });
}

/** A provider asking to confirm forwarding here: its link (opened in a new tab, by you) and code. */
function Confirmation({ env, m }: { env: Env; m: HeldMessage }) {
  const f = m.forwarding;
  const [dismiss, busy] = useHeldAction(env, "delete", [m.id]);
  if (!f) {
    return null;
  }
  const provider = t("held.provider.gmail");
  return (
    <li class="flex flex-col gap-3 rounded-lg border border-blue-300 bg-blue-50 p-4 text-sm text-blue-950">
      <p class="font-medium">
        {f.address
          ? t("held.forwarding.title", { provider, address: f.address })
          : t("held.forwarding.titleBare", { provider })}
      </p>
      <p class="text-blue-900">{t("held.forwarding.hint", { provider, address: m.triage_email_address })}</p>
      <div class="flex flex-wrap items-center gap-3">
        {f.link ? (
          <a href={f.link} target="_blank" rel="noopener noreferrer" class={buttonClass("primary", "sm")}>
            {t("held.forwarding.confirm")}
          </a>
        ) : null}
        {f.code ? (
          <span class="flex items-center gap-2">
            {t("held.forwarding.code")} <code class="rounded bg-white px-2 py-0.5 font-mono">{f.code}</code>
            <CopyButton text={f.code} label={t("common.copy")} />
          </span>
        ) : null}
        <Button size="sm" variant="ghost" busy={busy} onClick={() => dismiss()}>
          {t("held.forwarding.dismiss")}
        </Button>
      </div>
    </li>
  );
}

/** One held message: a line (subject, when, its start) that opens to the whole of it. */
function Message({ env, m }: { env: Env; m: HeldMessage }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        class="flex w-full items-start gap-2 px-4 py-2 text-left text-sm hover:bg-zinc-50"
      >
        <Chevron open={open} label={open ? t("email.fold") : t("email.unfold")} />
        <span class="min-w-0 flex-1">
          <span class="block truncate font-medium text-zinc-800">{m.subject}</span>
          <span class="block truncate text-zinc-500">{m.snippet}</span>
        </span>
        <span class="shrink-0 text-xs text-zinc-500">{formatRelative(m.received_at)}</span>
      </button>
      {open ? <Preview env={env} id={m.id} /> : null}
    </li>
  );
}

/** A held message read in place, as the email page shows mail (text only: it's anyone's), and its attachments' names. */
function Preview({ env, id }: { env: Env; id: string }) {
  const r = useResource(`held:${env.space.id}:${id}`, (signal) =>
    api.get<HeldMessage>(spaceApi(env.space, `/held/${id}`), undefined, signal),
  );
  return (
    <div class="px-4 pb-4">
      <Async r={r}>
        {(m) => {
          const files = (m.data["attachments"] as string[] | undefined) ?? [];
          return (
            <>
              <EmailView item={{ text: m.text ?? null, data: m.data }} url={undefined} />
              {files.length > 0 ? (
                <p class="mt-2 text-xs text-zinc-500">{t("held.attachments", { names: files.join(", ") })}</p>
              ) : null}
            </>
          );
        }}
      </Async>
    </div>
  );
}

/** Allowing a sender: the address or its whole domain; one whose mail isn't verified only with a second choice. */
function AllowDialog({ env, group, onClose }: { env: Env; group: HeldSender; onClose: () => void }) {
  const [domain, setDomain] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [allow, busy] = useAction(async () => {
    const r = await api.post<Allowed>(orgApi(env.space, "/allowlist"), {
      sender: group.sender,
      domain,
      unverified: !group.verified,
    });
    toast(t("held.allowed", { count: r.released, sender: domain ? (group.domain ?? "") : group.sender }));
    onClose();
    env.onChange();
  });
  const ready = group.verified || unverified;
  return (
    <div class="flex flex-col gap-4 text-sm">
      <p class="text-zinc-700">{t("held.allowExplain")}</p>
      {group.verified && group.domain ? (
        <Radios
          label={t("held.allowWhat")}
          value={domain ? "domain" : "address"}
          onValue={(v) => setDomain(v === "domain")}
          options={[
            { value: "address", label: t("held.allowAddress", { sender: group.sender }) },
            { value: "domain", label: t("held.allowDomain", { domain: group.domain }) },
          ]}
        />
      ) : null}
      {group.verified ? null : (
        <div class="flex flex-col gap-2 rounded-md bg-amber-50 p-3 text-amber-950">
          <p>{t("held.unverifiedWarning", { sender: group.sender })}</p>
          <Checkbox label={t("held.acceptUnverified")} checked={unverified} onCheck={setUnverified} />
        </div>
      )}
      <DialogActions>
        <Button answer="cancel" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" busy={busy} disabled={!ready} onClick={() => allow()}>
          {t("held.allow")}
        </Button>
      </DialogActions>
    </div>
  );
}

/** A sender's held mail: who (and whether it's verified), how much, and what can be done about it. */
function Sender({ env, group, admin }: { env: Env; group: HeldSender; admin: boolean }) {
  const [allowing, setAllowing] = useState(false);
  const ids = group.messages.map((m) => m.id);
  const [accept, accepting] = useHeldAction(env, "accept", ids);
  const [remove, removing] = useHeldAction(env, "delete", ids);
  const [block, blocking] = useAction(async () => {
    const r = await api.post<Blocked>(orgApi(env.space, "/blocklist"), { sender: group.sender });
    toast(t("held.blocked", { sender: group.sender, count: r.deleted }));
    env.onChange();
  });
  const latest = group.messages[0];
  return (
    <li class="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <div class="flex flex-wrap items-start gap-3 px-4 py-3">
        <div class="min-w-0 flex-1">
          <p class="flex flex-wrap items-center gap-2">
            <span class="font-medium break-all text-zinc-900">{group.sender}</span>
            {group.verified ? (
              <Badge tone="green" title={t("held.verifiedHint")}>
                {t("held.verified")}
              </Badge>
            ) : (
              <Badge tone="amber" title={t("held.unverifiedHint")}>
                {t("held.unverified")}
              </Badge>
            )}
          </p>
          <p class="text-xs text-zinc-500">
            {t("held.messages", { count: group.messages.length })}
            {latest ? ` · ${formatRelative(latest.received_at)}` : ""}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          {admin ? (
            <Button size="sm" variant="primary" onClick={() => setAllowing(true)}>
              {t("held.allow")}
            </Button>
          ) : null}
          <Button size="sm" busy={accepting} onClick={() => accept()}>
            {t("held.accept")}
          </Button>
          <Button size="sm" variant="danger" busy={removing} onClick={() => remove()}>
            {t("common.delete")}
          </Button>
          {admin ? (
            <ConfirmButton
              size="sm"
              variant="danger"
              label={t("held.block")}
              title={t("held.blockTitle", { sender: group.sender })}
              message={t("held.blockMessage")}
              confirmLabel={t("held.block")}
              busy={blocking}
              onConfirm={block}
            />
          ) : null}
        </div>
      </div>
      <ul class="divide-y divide-zinc-100 border-t border-zinc-200">
        {group.messages.map((m) => (
          <Message key={m.id} env={env} m={m} />
        ))}
      </ul>
      <Dialog open={allowing} onClose={() => setAllowing(false)} title={t("held.allowTitle", { sender: group.sender })}>
        <AllowDialog env={env} group={group} onClose={() => setAllowing(false)} />
      </Dialog>
    </li>
  );
}

/** A triage area tab's Unknown senders: one line on what this is (the rest folded), confirmations, then the senders. */
export function HeldPanel({ space, scope }: { space: SpaceOut; scope: HeldScope }) {
  const r = useResource(`held:${space.id}:${scope}`, (signal) =>
    api.get<Screen>(spaceApi(space, "/held"), { scope }, signal),
  );
  const env: Env = {
    space,
    scope,
    onChange: () => {
      r.reload();
      bump();
    },
  };
  const admin = atLeast(space.role, "admin");
  return (
    <Async r={r}>
      {(s) => (
        <>
          <div class="mb-4 max-w-2xl text-sm text-zinc-600">
            <Disclosure summary={t(admin ? "held.summary" : "held.summaryMember")}>
              <p class="mt-1 mb-2">{t(admin ? "held.intro" : "held.introMember", { days: s.keep_days })}</p>
            </Disclosure>
          </div>
          {s.confirmations.length > 0 ? (
            <ul class="mb-6 flex flex-col gap-3">
              {s.confirmations.map((m) => (
                <Confirmation key={m.id} env={env} m={m} />
              ))}
            </ul>
          ) : null}
          {s.senders.length === 0 && s.confirmations.length === 0 ? (
            <EmptyState title={t("held.empty")}>{t("held.emptyHint")}</EmptyState>
          ) : (
            <ul class="flex flex-col gap-4">
              {s.senders.map((g) => (
                <Sender key={`${g.sender}:${g.verified}`} env={env} group={g} admin={admin} />
              ))}
            </ul>
          )}
        </>
      )}
    </Async>
  );
}
